import { get, post } from './client';
import type {
  AdminUser,
  ListUsersResponse,
  ListUsersParams,
  CreateUserRequest,
} from '@/types/user';

/**
 * List all users with optional pagination.
 * Platform admin only.
 */
export async function listUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.paginationToken) queryParams.paginationToken = params.paginationToken;
  return get<ListUsersResponse>('/admin/users', queryParams);
}

/**
 * Get a single user by Cognito sub.
 */
export async function getUser(sub: string): Promise<AdminUser> {
  return get<AdminUser>(`/admin/users/${encodeURIComponent(sub)}`);
}

/**
 * Create a new user in the Cognito user pool.
 * Triggers an invitation email with temporary password.
 */
export async function createUser(data: CreateUserRequest): Promise<AdminUser> {
  return post<AdminUser>('/admin/users', data);
}

/**
 * Disable a user (revokes access, keeps account).
 */
export async function disableUser(sub: string): Promise<AdminUser> {
  return post<AdminUser>(`/admin/users/${encodeURIComponent(sub)}/disable`);
}

/**
 * Re-enable a previously disabled user.
 */
export async function enableUser(sub: string): Promise<AdminUser> {
  return post<AdminUser>(`/admin/users/${encodeURIComponent(sub)}/enable`);
}

/**
 * Reset a user's password — triggers a new temporary password email.
 */
export async function resetUserPassword(sub: string): Promise<void> {
  return post<void>(`/admin/users/${encodeURIComponent(sub)}/reset-password`);
}

/**
 * Reset a user's MFA — deactivates TOTP, renews grace period.
 * Platform admin only (Spec 0045).
 */
export async function resetUserMfa(sub: string): Promise<void> {
  return post<void>(`/admin/users/${encodeURIComponent(sub)}/reset-mfa`);
}

/**
 * Confirm MFA setup for the current user.
 * Server-seitig prueft, ob TOTP aktiv ist (Spec 0045).
 */
export async function confirmMfaSetup(): Promise<void> {
  return post<void>('/admin/users/me/mfa-setup-complete');
}

/**
 * Spec 525: Record a presence heartbeat for the current user (own sub).
 * Fire-and-forget from the UI — the server never-throws and returns 204.
 */
export async function sendPresenceHeartbeat(): Promise<void> {
  return post<void>('/admin/presence/heartbeat');
}
